import React, { useEffect } from 'react'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import {
  fetchLandingPage,
} from 'actions/ContentActions'
import { TYPE_OF_PAGE_BNBL, PAGE_NAME_TWC_LANDING } from 'constants/landingPage'
import { OFFER_TYPE_CRUISE_SLUG, OFFER_TYPE_TOUR_SLUG } from 'constants/offer'
import HttpStatus from 'components/Common/HttpStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import ErrorPage from 'components/Pages/ErrorPage'
import IconedCallout from 'components/Common/IconedCallout'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import PaneHeader from 'components/Common/Pane/PaneHeader'

import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import ImageHero from 'components/Common/ImageHero'
import HeadData from 'components/Common/HeadData'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import List from 'components/Common/List'
import LayoutContainer from 'components/Common/LayoutContainer'
import Faq from 'components/Common/Faq'
import { getContentPageKey } from 'lib/url/contentUtils'
import OfferListHeading from 'components/OfferList/OfferListHeading'
import TextButton from 'LuxKit/Button/TextButton'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import Group from 'components/utils/Group'

const TOUR_REDIRECT = `/${OFFER_TYPE_TOUR_SLUG}`
const TOUR_FILTER = [OFFER_TYPE_TOUR_SLUG, OFFER_TYPE_CRUISE_SLUG]

const TextHeading = styled(Heading)`
  position: relative;
`

const TextDescription = styled(BodyText)`
  position: relative;
  margin-top: ${rem(32)};
`

const EmpowermentTickIcon = styled(LineCheckIcon)`
  position: relative;
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

const SameHeightPaneSm = styled(Pane)`
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    margin-top: 0;
    height: 100%;
  }
`

const StyledList = styled(List)`
  margin-top: 0;
  margin-bottom: 0;
`

const StyledPaneBody = styled(PaneBody)`
  padding-top: 0;
  margin-top: 0;
`

const ThreeColumnGrid = styled.div`
  display: grid;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

interface Props {
  content: App.ContentBNBLPage
  error?: any
}

function TWCLandingPageContainer(props: Props) {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchLandingPage(PAGE_NAME_TWC_LANDING, TYPE_OF_PAGE_BNBL))
  }, [dispatch])

  if (props.error) {
    if (props.error.status == 404) {
      return (
        <HttpStatus code={404}>
          <Error404PageWithOffers />
        </HttpStatus>
      )
    } else {
      return <ErrorPage error={props.error} />
    }
  }

  if (!props.content) {
    return <LoadingIndicator />
  }

  const {
    header,
    empowerment,
    howItWorks,
    faq,
    offerList,
  } = props.content

  const offerListExtras: Array<OfferListV2Item> = [
    {
      id: 'heading',
      position: 'start',
      element: <OfferListHeading title={offerList.title} />,
      show: 'hasOffers',
    },
    {
      id: 'twc_discover_deals',
      position: 'end',
      element: (
        <Group direction="horizontal" horizontalAlign="center">
          <TextButton kind="primary" to={TOUR_REDIRECT}>
            {offerList.btnText}
          </TextButton>
        </Group>
      ),
    },
  ]

  return (
    <>
      <HeadData title={header.metaTitle || header.title} description={header.metaDescription || header.subTitle} />
      <ImageHero
        alt={header.title}
        aria-hidden="true"
        brightness={-15}
        gravity="auto"
        mobileAspectRatio="8:9"
        tabletAspectRatio="2:1"
        desktopAspectRatio="8:3"
        largeDesktopAspectRatio="3:1"
        id={header.image}
      >
        <LayoutContainer size="md">
          <TextHeading
            variant="heading3"
            align="center"
            forwardedAs="h1"
            colour="neutral-eight"
          >
            {header.title}
          </TextHeading>
          <TextDescription variant="large" align="center" forwardedAs="h2" colour="neutral-eight">
            {header.subTitle}
          </TextDescription>
        </LayoutContainer>
      </ImageHero>
      <Pane>
        <PaneHeader size="xl">
          <BodyText
            variant="large"
            align="center"
            as="h2"
          >
            {empowerment.title}
          </BodyText>
        </PaneHeader>
        <StyledPaneBody size="xl">
          <LayoutContainer size="sm">
            <StyledList type="unstyled" spaced>
              {empowerment.description.map(listItem => (
                <li key={listItem}>
                  <Group direction="horizontal" gap={16}>
                    <div>
                      <EmpowermentTickIcon size={26} colour="primary" />
                    </div>
                    {listItem}
                  </Group>
                </li>
              ))}
            </StyledList>
          </LayoutContainer>
        </StyledPaneBody>
      </Pane>
      <Pane background="neutral-six">
        <PaneHeader size="xl-y md">
          <BodyText
            variant="large"
            align="center"
            as="h2"
          >
            {howItWorks.title}
          </BodyText>
        </PaneHeader>
        <StyledPaneBody size="xl">
          <LayoutContainer>
            <ThreeColumnGrid>
              {howItWorks.description.map(item => (
                <SameHeightPaneSm key={item.title}>
                  <PaneBody size="lg xl-y">
                    <IconedCallout
                      icon={
                        <img
                          src={item.icon.url}
                          width={70}
                          height={70}
                          alt={item.icon.title}
                        >
                        </img>
                      }
                      title={item.title}
                      text={item.text}
                    />
                  </PaneBody>
                </SameHeightPaneSm>
              ))}
            </ThreeColumnGrid>
          </LayoutContainer>
        </StyledPaneBody>
      </Pane>
      {faq && <Pane>
        <PaneHeader size="xl-y md">
          <BodyText
            variant="large"
            align="center"
            as="h2"
          >
            {faq.title}
          </BodyText>
        </PaneHeader>
        <StyledPaneBody size="xl">
          <LayoutContainer>
            <Faq slug={faq.slug} />
          </LayoutContainer>
        </StyledPaneBody>
      </Pane>}
      <Pane background="neutral-six">
        <PaneBody size="lg">
          <LayoutContainer>
            <OfferList
              maxOffers={offerList.numberOfOffers}
              interstitials={offerListExtras}
              filters={{
                offerTypes: FLASH_OFFERS_TYPES,
                holidayTypes: TOUR_FILTER,
                frontPageVisibleOnly: true,
              }}
              random
            />
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </>
  )
}

const key = getContentPageKey(PAGE_NAME_TWC_LANDING, TYPE_OF_PAGE_BNBL)
function mapStateToProps(state: App.State) {
  return {
    content: state.content.landingPages[key],
    error: state.content.landingPageErrors[key],
  }
}

export default connect(
  mapStateToProps,
)(TWCLandingPageContainer)
